<script lang="ts" setup>
import type { NavLink } from '#core/types'
import DtConfirmModal from '#design-tokenomics/components/DtConfirmModal.vue'
import DtQuickAdjustSlideover from '#design-tokenomics/components/DtQuickAdjustSlideover.vue'
import { useDesignDocument } from '#design-tokenomics/composables/useDesignDocument'
import { useDesignRoot } from '#design-tokenomics/composables/useDesignRoot'
import { DESIGN_SECTIONS } from '#design-tokenomics/utils/sections'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@forgd/ui-kit/accordion'
import { Badge } from '@forgd/ui-kit/badge'
import { CheckCircleIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'
import { createReusableTemplate, useTimeAgo } from '@vueuse/core'

useHead({
  title: 'Design Tokenomics',
})

const route = useRoute()

watch(
  () => route.fullPath,
  () => {
    route.meta.pageHeader = {
      description: 'Free Tools',
      title: 'Design Tokenomics',
      icon: '/icons/design-tokenomics.svg',
    }
  },
  { immediate: true },
)

const { root, sectionPath } = useDesignRoot()
const design = useDesignDocument()
const { model, updateModel, isCompleted, lastEditedAt } = design

const dashboardPath = root

const activeSection = computed(() =>
  DESIGN_SECTIONS.find(section => section.pages.some(page => sectionPath(page.path) === route.path))?.label,
)

const openSection = ref(activeSection.value)

watch(activeSection, (label) => {
  if (label) {
    openSection.value = label
  }
})

const lastEditedAgo = useTimeAgo(computed(() => (lastEditedAt.value ? new Date(lastEditedAt.value) : new Date())))

const lastEdited = computed(() => (lastEditedAt.value ? lastEditedAgo.value : null))

const [DefineNavLinks, ReuseNavLinks] = createReusableTemplate()

const mobileNavItems = computed<NavLink[]>(() => [
  { label: 'Dashboard', to: dashboardPath.value },
  ...DESIGN_SECTIONS.flatMap(section => section.pages.flatMap(page => page.path ? [{ label: page.label, to: sectionPath(page.path) }] : [])),
])
</script>

<template>
  <DefineNavLinks>
    <NuxtLink
      :to="dashboardPath"
      class="flex h-8.5 w-full items-center rounded-sm p-2 hover:bg-neutral-400"
      :class="[route.path === dashboardPath ? 'bg-neutral-400' : '']"
    >
      <span class="text-xs font-normal whitespace-nowrap text-gray-600">Dashboard</span>
    </NuxtLink>

    <Accordion v-model="openSection" type="single" collapsible>
      <AccordionItem
        v-for="section in DESIGN_SECTIONS"
        :key="section.label"
        :value="section.label"
        class="border-b-0"
      >
        <AccordionTrigger
          class="mb-1 rounded-sm p-2 text-xs font-semibold text-primal hover:bg-neutral-400"
        >
          {{ section.label }}
          <template #icon>
            <ChevronDownIcon
              class="size-4 shrink-0 text-primal transition-transform group-data-[state=open]:rotate-180"
            />
          </template>
        </AccordionTrigger>
        <AccordionContent class="space-y-1 p-0">
          <template v-for="page in section.pages" :key="page.slug">
            <NuxtLink
              v-if="page.path"
              :to="sectionPath(page.path)"
              class="flex h-8.5 w-full items-center gap-1 rounded-sm px-1 py-2 hover:bg-neutral-400"
              :class="[route.path === sectionPath(page.path) ? 'bg-neutral-400' : '']"
            >
              <CheckCircleIcon
                class="size-3.5 shrink-0"
                :class="isCompleted(page.slug) ? 'text-accent' : 'text-placeholder'"
              />
              <span class="text-xs whitespace-nowrap text-secondary">{{ page.label }}</span>
            </NuxtLink>

            <div v-else class="flex h-8.5 w-full items-center gap-2 p-2 pl-3">
              <CheckCircleIcon class="size-3.5 shrink-0 text-placeholder" />
              <span class="text-xs whitespace-nowrap text-placeholder">{{ page.label }}</span>
            </div>
          </template>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </DefineNavLinks>

  <NuxtLayout
    name="dashboard"
    supports-mobile
  >
    <template #header>
      <div class="flex items-center gap-3">
        <img
          src="/icons/design-tokenomics.svg"
          width="32"
          height="32"
          alt="Design Tokenomics"
        >
        <div class="flex shrink-0 flex-col justify-center">
          <span class="text-xs leading-none font-normal text-secondary">
            Free Tools
          </span>
          <span class="text-sm font-semibold text-primal">
            Design Tokenomics
          </span>
        </div>
        <Badge
          v-if="lastEdited"
          variant="neutral"
          class="border-default bg-transparent text-current"
        >
          {{ `Last edited: ${lastEdited}` }}
        </Badge>
      </div>
    </template>

    <template #sidebar>
      <div class="h-full w-sidebar border-r border-default max-md:hidden">
        <nav aria-label="Design Tokenomics Navigation" class="sticky top-0 flex flex-col gap-1 px-2 py-4">
          <ReuseNavLinks />
        </nav>
      </div>
    </template>

    <template #subheader>
      <CoreMobileNav :items="mobileNavItems" label="Design Tokenomics" lock-scroll>
        <nav aria-label="Design Tokenomics Navigation" class="flex flex-col gap-1">
          <ReuseNavLinks />
        </nav>
      </CoreMobileNav>
    </template>

    <div class="@container mx-auto mb-5 w-full max-w-300 min-w-0">
      <slot />
    </div>

    <DtQuickAdjustSlideover
      v-if="model"
      :model="model"
      @save="updateModel"
    />

    <DtConfirmModal />

    <template #app-footer>
      <CoreAppFooterBlock />
    </template>
  </NuxtLayout>
</template>
